import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { TILES } from "../CanadaTileMap";
import { PROVINCE_NAMES } from "../../lib/tone";

const fmt = (n) => (n ?? 0).toLocaleString("en-CA");
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const Card = ({ title, children, testId, className = "" }) => (
  <section className={`min-w-0 rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 ${className}`} data-testid={testId}>
    {title && <h2 className="text-lg font-bold text-slate-900">{title}</h2>}{children}
  </section>
);

export const MomentumBadge = ({ m, testId }) => {
  const Icon = m.status === "Rising" ? TrendingUp : m.status === "Declining" ? TrendingDown : Minus;
  const tone = m.status === "Rising" ? "bg-emerald-50 text-emerald-800" : m.status === "Declining" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`} data-testid={testId}><Icon size={13} />{m.status} {m.change_pct > 0 ? "+" : ""}{m.change_pct}%</span>;
};

export const MetricsGrid = ({ m, cats }) => {
  const items = [["Active Opportunities", m.active_opportunities], ["Upcoming Events", m.upcoming_events], ["Active Fundraisers", m.active_fundraisers],
    ["Participating Organizations", m.participating_organizations], ["Total Interested Actions", m.total_interested], ["Total Attended Actions", m.total_attended],
    ["New Opportunities This Week", m.new_this_week]];
  const active = cats.filter((c) => c.interested + c.attended > 0).slice(0, 3);
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="action-metrics">
      {items.map(([l, v]) => (
        <div key={l} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`metric-${slug(l)}`}>
          <p className="text-3xl font-bold tracking-tight text-[#1E3A8A]">{fmt(v)}</p><p className="mt-1 text-sm text-slate-700">{l}</p>
        </div>
      ))}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="metric-most-active-categories">
        <p className="text-sm font-semibold text-slate-900">Most Active Categories</p>
        {active.length ? <ol className="mt-2 space-y-0.5 text-sm text-slate-700">{active.map((c) => <li key={c.name}>{c.rank}. {c.name}</li>)}</ol>
          : <p className="mt-2 text-sm text-slate-600">No activity yet this period</p>}
      </div>
    </div>
  );
};

export const CategoryRanking = ({ cats }) => (
  <Card title="Top Action Categories" testId="category-ranking">
    <p className="mt-1 text-sm text-slate-600">Ranked by Interested + Attended actions this period, with Action Momentum™ versus the previous period.</p>
    <ol className="mt-4 divide-y divide-slate-100">
      {cats.map((c) => (
        <li key={c.name} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3" data-testid={`category-rank-${slug(c.name)}`}>
          <span className="w-8 text-lg font-bold text-[#1E3A8A]">#{c.rank}</span>
          <span className="min-w-[10rem] flex-1 font-semibold text-slate-900">{c.name}</span>
          <span className="text-sm text-slate-700">Interested: <b>{fmt(c.interested)}</b></span>
          <span className="text-sm text-slate-700">Attended: <b>{fmt(c.attended)}</b></span>
          <MomentumBadge m={c} testId={`category-momentum-${slug(c.name)}`} />
        </li>
      ))}
    </ol>
  </Card>
);

export const ActionHeatMap = ({ provinces, metric, setMetric }) => {
  const by = Object.fromEntries(provinces.map((p) => [p.code, p]));
  const val = (p) => (p ? (metric === "all" ? p.interested + p.attended : p[metric]) : 0);
  const max = Math.max(1, ...provinces.map(val));
  return (
    <Card title="Action Heat Map" testId="action-heat-map">
      <div className="mt-3 flex flex-wrap gap-2">
        {[["all", "All actions"], ["interested", "Interested"], ["attended", "Attended"]].map(([k, l]) => (
          <button key={k} onClick={() => setMetric(k)} aria-pressed={metric === k} data-testid={`heat-metric-${k}`}
            className={`rounded-full border px-3 py-1 text-sm ${metric === k ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 text-slate-700"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-4 overflow-x-auto">
        <svg viewBox="0 0 800 390" className="h-auto w-full min-w-[480px]" role="img" aria-label="Participation by province and territory">
          {Object.entries(TILES).map(([code, [x, y, w, h]]) => {
            const v = val(by[code]);
            const a = v ? 0.15 + 0.85 * (v / max) : 0;
            return (
              <g key={code} data-testid={`heat-tile-${code}`}>
                <title>{`${PROVINCE_NAMES[code]}: ${v} actions`}</title>
                <rect className="heat-tile" x={x} y={y} width={w} height={h} rx={14} fill={v ? `rgba(46,125,50,${a})` : "#f1f5f9"} stroke="#cbd5e1" strokeWidth={1.5} />
                <text x={x + w / 2} y={y + h / 2 + 5} textAnchor="middle" className={`text-[22px] font-bold ${a > 0.55 ? "fill-white" : "heat-label fill-slate-800"}`}>{code} {v}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <p className="mt-2 text-xs text-slate-600">Darker green means more Interested and Attended actions on listings in that region this period.</p>
    </Card>
  );
};
